/**
 * @fileoverview Input/output schemas for the world-element-image-prompt-creator-v2 agent.
 *
 * This agent takes world element details and creates a rich, artistic prompt
 * for an image generation model. Supports visual element types: place, set,
 * object, and species.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Uses a unified schema that accepts any visual world element type.
 * The element's details are passed as a key-value record (from snapshot fields).
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storySetting.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .extend({
    elementName: z.string().describe("The name of the world element."),
    elementType: z.enum(['place', 'set', 'object', 'species']).describe("The world element type."),
    elementSubtype: z.string().describe("The element subtype (e.g., 'location', 'building', 'artifact', 'creature')."),
    elementDetails: z.record(z.string()).describe("A key-value object of details from the element's latest snapshot or initial description."),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  prompt: z.string().describe('The generated, optimized image prompt for this world element.'),
});

export type Output = z.infer<typeof OutputSchema>;
